import { FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { faCoin } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { AiApplyType, SelectType } from "@/enum/list.enum";
import { PermissionRoles } from "@/enum";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { profileTrackerSelector } from "@/pages/Private/redux/profileTracker/profileTracker.slice";
import { useAppSelector } from "@/redux/hooks";
import { ERROR_TYPE, renderErrorMessages } from "@/utils";
import {
	useGetDeepResearchPromptsQuery,
	useExecuteBulkDeepResearchMutation,
} from "@/pages/Private/redux/deepResearch/deepResearch.api";

import { getCurrentPrices } from "@/pages/Private/pages/List/utils/currentPrices";

import { useDeepResearchSocketContext } from "../../../providers/DeepResearchSocketContext";

import { IFilterWithValues } from "../../FilterModal";
import { Tracker } from "../../../schema/profileTracker";

export interface DeepResearchModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	/**
	 * The list whose tracked companies the run covers: the bulk endpoint's `listId`, which its
	 * "apply to all" path loads as a list. Never the tracker's id (PREC-4660): that names another
	 * list, so the run 404'd or hit another company's list.
	 */
	listId?: number;
	setOpen: (value: boolean) => void;
	selectedTrackerOrganizationIds?: number[];
	tracker?: Tracker;
	filter?: IFilterWithValues;
}

export const DeepResearchModal: FunctionComponent<DeepResearchModalProps> = ({
	setShowConfirm,
	listId,
	open,
	setOpen,
	selectedTrackerOrganizationIds,
	tracker,
	filter,
}) => {
	const { hasPermissions } = useHasPermissions();
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);
	const { startPending } = useDeepResearchSocketContext();

	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);
	const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);

	const { records, recordsMeta } = useAppSelector(profileTrackerSelector);

	const { data: promptsData } = useGetDeepResearchPromptsQuery({ page: 1, limit: 100 });
	const [executeBulk, { isLoading, error }] = useExecuteBulkDeepResearchMutation();

	const prompts = promptsData?.data || [];
	const activePromptId = selectedPromptId ?? prompts[0]?.id ?? null;

	const trackerOrganizationIds = useMemo(() => {
		if (applyTo === AiApplyType.FIRST_10) {
			return records.slice(0, 10).map((p) => p.id);
		}

		if (applyTo === AiApplyType.CURRENTLY_SELECTED) {
			return selectedTrackerOrganizationIds ?? [];
		}

		return [];
	}, [applyTo, records, selectedTrackerOrganizationIds]);

	const selectType = useMemo(() => {
		if (applyTo === AiApplyType.FIRST_10 || applyTo === AiApplyType.CURRENTLY_SELECTED) {
			return SelectType.CURRENT_PAGE;
		}

		return SelectType.ALL;
	}, [applyTo]);

	const applyToButtonText = useMemo(() => {
		return applyTo === AiApplyType.NONE
			? ts(`button.${AiApplyType.NONE}`)
			: applyTo === AiApplyType.FIRST_10
				? ts(`button.${AiApplyType.FIRST_10}`)
				: applyTo === AiApplyType.CURRENTLY_FILTERED
					? ts(`button.${AiApplyType.CURRENTLY_FILTERED}`)
					: ts(`button.${AiApplyType.ALL}`);
	}, [applyTo, ts]);

	const currentCreditCost = useMemo(() => {
		return getCurrentPrices({
			applyTo,
			meta: recordsMeta,
			singlePrice: 1,
			selectedProspectIds: selectedTrackerOrganizationIds,
			tracker: tracker,
		});
	}, [applyTo, recordsMeta, selectedTrackerOrganizationIds, tracker]);

	const onSubmit = async () => {
		if (!activePromptId || applyTo === AiApplyType.NONE || listId === undefined) {
			return;
		}

		try {
			await executeBulk({
				listId,
				selectType,
				trackerOrganizationIds,
				deepResearchPromptId: activePromptId,
				...(selectType === SelectType.ALL && filter ? { filter } : {}),
			}).unwrap();

			// Mark prospects as pending for FIRST_10 / CURRENTLY_SELECTED
			if (applyTo === AiApplyType.FIRST_10 || applyTo === AiApplyType.CURRENTLY_SELECTED) {
				const matchedOrgs = records.filter((r) => trackerOrganizationIds.includes(r.id));

				for (const org of matchedOrgs) {
					for (const tp of org.trackerPersons ?? []) {
						if (tp.prospect?.id) {
							startPending(tp.prospect.id);
						}
					}
				}
			}

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const apiErrors = error
		? ([
				"message" in error ? (error as { message: string }).message : "An error occurred",
			] as ERROR_TYPE[])
		: [];

	const promptDropdownData = useMemo(
		() =>
			prompts.map((p) => ({
				title: p.name,
				id: p.id.toString(),
			})),
		[prompts]
	);

	return (
		<Modal
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={onSubmit}
			isLoading={isLoading}
			isOpened={open}
			size="xs"
			submitButtonText={applyToButtonText}
			title="Deep Research"
		>
			<div className="flex flex-col space-y-4">
				<Dropdown
					data={promptDropdownData}
					floating={false}
					handleSelect={(value?: AutoCompleteItem) => {
						if (value?.id) {
							setSelectedPromptId(Number(value.id));
						}
					}}
					label="Deep Research Prompt"
					value={
						activePromptId
							? promptDropdownData.find((p) => p.id === activePromptId.toString())
							: undefined
					}
				/>

				{apiErrors.length ? renderErrorMessages(apiErrors) : null}
			</div>
			<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
				<Dropdown
					data={[
						{
							title: ts(AiApplyType.NONE),
							id: AiApplyType.NONE,
						},
						{
							title: ts(AiApplyType.FIRST_10),
							id: AiApplyType.FIRST_10,
						},
						{
							title: ts(AiApplyType.CURRENTLY_FILTERED),
							id: AiApplyType.CURRENTLY_FILTERED,
						},
						{
							title: ts(AiApplyType.CURRENTLY_SELECTED),
							id: AiApplyType.CURRENTLY_SELECTED,
						},
						{
							title: ts(AiApplyType.ALL),
							id: AiApplyType.ALL,
						},
						{
							title: ts(AiApplyType.ALL_INCLUDING_ASSIGNED),
							id: AiApplyType.ALL_INCLUDING_ASSIGNED,
						},
					]}
					floating={false}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setApplyTo(value.id as AiApplyType);
						}
					}}
					label="Apply to ..."
					value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
				/>
			</div>
			{hasPermissions([PermissionRoles.GLOBAL_ADMIN]) && (
				<>
					<div className="flex flex-row mt-5 border-t pt-5 border-gray-200 text-gray-700 items-center text-sm">
						<span className="mr-1">1x</span>
						<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
						<span>per prospect</span>
					</div>
					{applyTo && currentCreditCost > 0 && (
						<div className="flex flex-row pt-5 text-gray-700 items-center text-sm">
							<span className="mr-1">{currentCreditCost}</span>
							<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
							<span>total</span>
						</div>
					)}
				</>
			)}
		</Modal>
	);
};
